/** Locale-aware formatting helpers. Locale is "ar" or "en"; both use Western digits. */

const numberLocale = (lang: string) => (lang === "ar" ? "ar-EG-u-nu-latn" : "en-US");

export function formatCount(value: number, lang: string): string {
  return new Intl.NumberFormat(numberLocale(lang), {
    notation: value >= 10_000 ? "compact" : "standard",
    maximumFractionDigits: 1,
  }).format(value);
}

export function formatBytes(bytes: number | null | undefined, lang: string): string {
  if (bytes == null) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const formatted = new Intl.NumberFormat(numberLocale(lang), {
    maximumFractionDigits: value < 10 ? 1 : 0,
  }).format(value);
  return formatted + " " + units[unit];
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  const total = Math.round(seconds);
  const minutes = Math.floor(total / 60);
  return minutes + ":" + String(total % 60).padStart(2, "0");
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 31_536_000],
  ["month", 2_592_000],
  ["week", 604_800],
  ["day", 86_400],
  ["hour", 3600],
  ["minute", 60],
  ["second", 1],
];

export function formatRelative(iso: string | null | undefined, lang: string): string {
  if (!iso) return "—";
  const elapsed = (Date.parse(iso) - Date.now()) / 1000;
  const formatter = new Intl.RelativeTimeFormat(numberLocale(lang), { numeric: "auto" });
  for (const [unit, secondsInUnit] of RELATIVE_UNITS) {
    if (Math.abs(elapsed) >= secondsInUnit || unit === "second") {
      return formatter.format(Math.round(elapsed / secondsInUnit), unit);
    }
  }
  return "—";
}

export function formatDateTime(iso: string | null | undefined, lang: string): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(numberLocale(lang), {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));
}

/** Grapheme-ish character count, matching the character limits the backend enforces. */
export function charLength(value: string): number {
  return Array.from(value.normalize("NFC")).length;
}

export function initials(username: string): string {
  return username.slice(0, 2).toUpperCase();
}

/** Deterministic hue from a string, so avatar placeholders are stable per user. */
export function hueFor(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) % 360;
  return hash;
}
